// src/components/Admin/Donors/hooks/useDonors.ts

import { useState, useEffect, useCallback } from "react";
import { donationService } from "../../../services/api/donationService";
import type { Donor, Donation, DonationStatus, EnhancedDonor } from "../../../types/donation";
import { useAuth } from "../../../contexts/AuthContext";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

interface DecodedToken {
  exp: number;
  // Add other token fields if needed
}

export const useDonors = () => {
  const { tokens, signOut } = useAuth();

  // States
  const [donors, setDonors] = useState<EnhancedDonor[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedDonor, setSelectedDonor] = useState<EnhancedDonor | null>(null);
  const [donorDonations, setDonorDonations] = useState<{
    donations: Donation[];
    count: number;
    lastEvaluatedKey: string | null;
  }>({
    donations: [],
    count: 0,
    lastEvaluatedKey: null,
  });
  const [donorStats, setDonorStats] = useState({
    totalDonors: 0,
    majorDonors: 0,
    repeatDonors: 0,
  });

  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      const decoded: DecodedToken = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true; // If token is invalid, treat it as expired
    }
  }, []);

  const loadDonors = useCallback(async () => {
    if (!tokens?.accessToken) {
      setError("No authentication token found. Please log in again.");
      return;
    }

    if (isTokenExpired(tokens.accessToken)) {
      setError("Session expired. Please log in again.");
      signOut();
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await donationService.getDonors(tokens.accessToken);
      // console.log("Donors:", response);

      if (!response?.donors || !Array.isArray(response.donors)) {
        throw new Error("Invalid response format from server");
      }

      const enhancedDonors: EnhancedDonor[] = response.donors.map((donor: Donor) => ({
        ...donor,
        name: `${donor.firstName || ""} ${donor.lastName || ""}`.trim() || "Unknown",
        donationCount: donor.donationCount || 0,
        totalDonations: donor.totalDonations || 0,
      }));

      // Calculate statistics
      const majorDonors = enhancedDonors.filter((donor) => donor.totalDonations > 1000).length;
      const repeatDonors = enhancedDonors.filter((donor) => donor.donationCount > 1).length;

      setDonorStats({
        totalDonors: response.count || enhancedDonors.length,
        majorDonors,
        repeatDonors,
      });

      setDonors(enhancedDonors);
    } catch (err: any) {
      console.error("Failed to load donors:", err);
      setError(err.message || "Unable to load donors. Please try again later.");
      toast.error(err.message || "Unable to load donors. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [tokens, isTokenExpired, signOut]);

  const handleViewDonations = useCallback(
    async (donorId: string) => {
      if (!tokens?.idToken) {
        setError("No authentication token found. Please log in again.");
        return;
      }

      if (isTokenExpired(tokens.idToken)) {
        setError("Session expired. Please log in again.");
        signOut();
        return;
      }

      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const donationsResponse = await donationService.getDonorDonations(tokens.idToken, donorId);
        setDonorDonations(donationsResponse);

        const donor = donors.find((d) => d.id === donorId) || null;
        setSelectedDonor(donor);
      } catch (err: any) {
        console.error("Failed to load donor donations:", err);
        setError(err.message || "Unable to load donor donations.");
        toast.error(err.message || "Unable to load donor donations.");
      } finally {
        setLoading(false);
      }
    },
    [tokens, isTokenExpired, signOut, donors]
  );

  const handleUpdateDonationStatus = useCallback(
    async (donationId: string, newStatus: DonationStatus, reason?: string) => {
      if (!tokens?.idToken) {
        setError("No authentication token found. Please log in again.");
        return;
      }

      if (isTokenExpired(tokens.idToken)) {
        setError("Session expired. Please log in again.");
        signOut();
        return;
      }

      setLoading(true);
      setError(null);
      setSuccess(null); // Reset success message

      try {
        await donationService.updateDonationStatus(tokens.idToken, donationId, {
          status: newStatus,
          reason,
        });

        // Refresh donor donations if modal is open
        if (selectedDonor) {
          const donationsResponse = await donationService.getDonorDonations(tokens.idToken, selectedDonor.id);
          setDonorDonations(donationsResponse);
        }

        setSuccess("Donation status updated successfully.");
        toast.success("Donation status updated successfully.");
      } catch (err: any) {
        console.error("Failed to update donation status:", err);
        setError(err.message || "Unable to update donation status.");
        toast.error(err.message || "Unable to update donation status.");
      } finally {
        setLoading(false);
      }
    },
    [tokens, isTokenExpired, signOut, selectedDonor]
  );

  useEffect(() => {
    loadDonors();
  }, [loadDonors]);

  return {
    donors,
    loading,
    error,
    success,
    donorStats,
    selectedDonor,
    setSelectedDonor,
    donorDonations,
    loadDonors,
    handleViewDonations,
    handleUpdateDonationStatus,
  };
};
